import React, { useEffect, useMemo } from 'react';
import L from 'leaflet';
import { CircleMarker, MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { JOURNEY_TYPES } from './TripJourneyTimeline';

// Trip map in the Trips details popup - same Google tiles as the Activity page. Shows the start (A)
// and end (B) points, and, when the trip has a journey log, the driver's position at each logged
// action (the selected action is enlarged and the map flies to it).

const pointIcon = (label, className) => L.divIcon({
    className: 'trip-point-icon',
    html: `<div class="trip-point ${className}"><span>${label}</span></div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
    popupAnchor: [0, -17],
});
const START_ICON = pointIcon('A', 'start');
const END_ICON = pointIcon('B', 'end');

const toLatLng = (p) => {
    const lat = Number(p?.latitude);
    const lng = Number(p?.longitude);
    return Number.isFinite(lat) && Number.isFinite(lng) ? [lat, lng] : null;
};

const FitAndFocus = ({ bounds, focus }) => {
    const map = useMap();
    useEffect(() => {
        // The popup animates in; recompute the size once it has its final dimensions.
        const t = setTimeout(() => {
            map.invalidateSize();
            if (bounds.length > 1) map.fitBounds(bounds, { padding: [40, 40] });
            else if (bounds.length === 1) map.setView(bounds[0], 14);
        }, 120);
        return () => clearTimeout(t);
    }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (focus) map.flyTo(focus, Math.max(map.getZoom(), 15), { duration: 0.6 });
    }, [map, focus?.[0], focus?.[1]]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
};

const TripMap = ({ trip, events = [], selectedIndex = null, onSelectEvent }) => {
    const start = toLatLng(trip?.startPoint);
    const end = toLatLng(trip?.endingPoint);
    const eventPoints = useMemo(
        () => events.map((e, index) => ({ index, event: e, pos: toLatLng(e.driverLocation) })).filter((p) => p.pos),
        [events]
    );
    const bounds = useMemo(
        () => [start, end, ...eventPoints.map((p) => p.pos)].filter(Boolean),
        [start?.[0], start?.[1], end?.[0], end?.[1], eventPoints] // eslint-disable-line react-hooks/exhaustive-deps
    );
    const selected = eventPoints.find((p) => p.index === selectedIndex);
    const center = start || end || [32.8872, 13.1913];

    return (
        <MapContainer
            center={center}
            zoom={13}
            minZoom={3}
            maxZoom={18}
            scrollWheelZoom
            style={{ height: '320px', width: '100%' }}
        >
            <TileLayer
                url="https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}"
                subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
                attribution="Map data © Google"
            />
            <FitAndFocus bounds={bounds} focus={selected?.pos || null} />

            {start && end ? <Polyline positions={[start, end]} pathOptions={{ color: '#2563eb', weight: 3, opacity: 0.7, dashArray: '6 6' }} /> : null}
            {start ? (
                <Marker position={start} icon={START_ICON}>
                    <Popup><strong>نقطة البداية:</strong> {trip.startPoint.address}</Popup>
                </Marker>
            ) : null}
            {end ? (
                <Marker position={end} icon={END_ICON}>
                    <Popup><strong>نقطة النهاية:</strong> {trip.endingPoint.address}</Popup>
                </Marker>
            ) : null}

            {eventPoints.map(({ index, event, pos }) => {
                const info = JOURNEY_TYPES[event.type] || { label: event.type, color: '#64748b' };
                const isSelected = index === selectedIndex;
                return (
                    <CircleMarker
                        key={`${index}-${event.at}`}
                        center={pos}
                        radius={isSelected ? 10 : 6}
                        pathOptions={{ color: '#fff', weight: 2, fillColor: info.color, fillOpacity: 1 }}
                        eventHandlers={{ click: () => onSelectEvent?.(index) }}
                    >
                        <Tooltip direction="top" offset={[0, -6]} permanent={isSelected}>
                            {info.label}
                        </Tooltip>
                    </CircleMarker>
                );
            })}
        </MapContainer>
    );
};

export default TripMap;
